import React, { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Eye, EyeOff } from 'lucide-react'
import { supabase } from '../lib/supabase'
import { toast } from 'sonner'

const schema = z.object({
  email:    z.string().email('Email invalide'),
  password: z.string().min(6, 'Mot de passe requis'),
})

const BG_IMAGE = 'https://images.unsplash.com/photo-1618858553936-84170aa4be58?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200'
const LOGO_WHITE = '/logo-white.png'
const LOGO_BLUE = '/logo-blue.png'

export default function LoginPage() {
  const [showPwd, setShowPwd] = useState(false)
  const [loading, setLoading] = useState(false)

  const { register, handleSubmit, formState: { errors } } = useForm({
    resolver: zodResolver(schema),
  })

  async function onSubmit({ email, password }) {
    setLoading(true)
    const { error } = await supabase.auth.signInWithPassword({ email, password })
    if (error) {
      toast.error('Identifiants incorrects. Vérifiez votre email et mot de passe.')
      setLoading(false)
    }
    // On success, AuthContext listener handles redirect
  }

  return (
    <div className="h-screen flex overflow-hidden">
      {/* Left — Hero image */}
      <div
        className="hidden lg:flex lg:w-1/2 relative flex-col justify-between p-12"
        style={{ background: '#0F172A' }}
      >
        <div
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage: `url(${BG_IMAGE})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        />
        <div className="relative z-10">
          <img 
            src={LOGO_WHITE} 
            alt="C2 Assurances" 
            className="h-10 w-auto"
            data-testid="login-logo-desktop"
          />
        </div>
        <div className="relative z-10">
          <h1
            className="text-4xl font-bold text-white leading-tight"
            style={{ fontFamily: 'Manrope, sans-serif' }}
          >
            Pilotage assurantiel<br />
            <span className="text-indigo-400">unifié et intelligent</span>
          </h1>
        </div>
      </div>

      {/* Right — Login form */}
      <div className="flex-1 flex items-center justify-center bg-white px-8">
        <div className="w-full max-w-sm">
          {/* Mobile logo */}
          <div className="mb-8 lg:hidden">
            <img 
              src={LOGO_BLUE} 
              alt="C2 Assurances" 
              className="h-9 w-auto"
              data-testid="login-logo-mobile"
            />
          </div>

          <div className="mb-8">
            <h2
              className="text-2xl font-bold text-slate-900 mb-1"
              style={{ fontFamily: 'Manrope, sans-serif' }}
            >
              Connexion
            </h2>
            <p className="text-sm text-slate-500">
              Accédez à votre espace de gestion
            </p>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-5" data-testid="login-form">
            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">
                Adresse email
              </label>
              <input
                {...register('email')}
                type="email"
                placeholder="votre@email.fr"
                autoComplete="email"
                data-testid="login-email-input"
                className={`w-full h-10 px-3 rounded-md border text-sm bg-white outline-none transition-all
                  focus:ring-2 focus:ring-slate-900 focus:ring-offset-1
                  ${errors.email ? 'border-red-400 bg-red-50' : 'border-slate-200'}`}
              />
              {errors.email && (
                <p className="mt-1 text-xs text-red-500">{errors.email.message}</p>
              )}
            </div>

            {/* Password */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">
                Mot de passe
              </label>
              <div className="relative">
                <input
                  {...register('password')}
                  type={showPwd ? 'text' : 'password'}
                  placeholder="••••••••"
                  autoComplete="current-password"
                  data-testid="login-password-input"
                  className={`w-full h-10 px-3 pr-10 rounded-md border text-sm bg-white outline-none transition-all
                    focus:ring-2 focus:ring-slate-900 focus:ring-offset-1
                    ${errors.password ? 'border-red-400 bg-red-50' : 'border-slate-200'}`}
                />
                <button
                  type="button"
                  onClick={() => setShowPwd(!showPwd)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                >
                  {showPwd ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              {errors.password && (
                <p className="mt-1 text-xs text-red-500">{errors.password.message}</p>
              )}
            </div>

            <button
              type="submit"
              disabled={loading}
              data-testid="login-submit-button"
              className="w-full h-10 rounded-md bg-slate-900 text-white text-sm font-semibold
                hover:bg-slate-800 active:bg-slate-950 transition-colors
                disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Connexion...
                </>
              ) : 'Se connecter'}
            </button>
          </form>

          <div className="mt-10 text-center">
            <p className="text-xs text-slate-400">
              &copy; {new Date().getFullYear()} C2 Assurances &mdash; Tous droits réservés
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
